'use client';

import { useEffect, useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

//test after new domain

interface ProjectModalProps {
  project: {
    id: string;
    title: string;
    location: string;
    type: string;
    style: string;
    budget: string;
    area: string;
    image: string;
    alt: string;
    description: string;
    completionYear: number;
    detailedDescription: string;
    challenges: string[];
    solutions: string[];
    materials: string[];
    timeline: string;
    clientTestimonial: string;
    clientName: string;
    beforeImage: string;
    beforeAlt: string;
    gallery: Array<{ image: string; alt: string }>;
    videos?: Array<{ url: string; title: string }>;
  } | null;
  isOpen: boolean;
  onClose: () => void;
}

const ProjectModal = ({ project, isOpen, onClose }: ProjectModalProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'process' | 'gallery'>('overview');
  const [playingVideoId, setPlayingVideoId] = useState<string | null>(null);

  // Mounted controls DOM presence while animating
  const [mounted, setMounted] = useState(false);
  // animate controls visible classes
  const [animate, setAnimate] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | undefined;

    if (isOpen && project) {
      setMounted(true);
      // allow DOM to mount before starting animation
      requestAnimationFrame(() => setAnimate(true));
    } else {
      // trigger exit animation
      setAnimate(false);
      setPlayingVideoId(null);
      // unmount after animation completes
      timeout = setTimeout(() => setMounted(false), 300);
    }

    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [isOpen, project]);

  useEffect(() => {
    if (mounted) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mounted]);

  // close on Escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && mounted) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mounted, onClose]);

  const getYouTubeVideoId = (url: string): string | null => {
    const match = url.match(/(?:shorts\/|watch\?v=|youtu\.be\/|embed\/)([A-Za-z0-9_-]{11})/);
    return match ? match[1] : null;
  };

  if (!isHydrated || !mounted || !project) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className={`absolute inset-0 bg-black/80 backdrop-blur-sm transition-opacity duration-300 ${animate ? 'opacity-100' : 'opacity-0'}`}
        onClick={onClose}
      />
      
      <div
        className={`relative w-full max-w-6xl max-h-[90vh] bg-white rounded-2xl overflow-hidden transform transition-all duration-300 origin-center ${animate ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-6 scale-95'}`}
        style={{ willChange: 'transform, opacity' }}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-2 bg-white/90 backdrop-blur-sm rounded-full text-black hover:text-[#1B5E4F] transition-colors duration-200"
        >
          <Icon name="XMarkIcon" size={24} />
        </button>

        <div className="overflow-y-auto max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
          <div className="relative h-96">
            <AppImage
              src={project.image}
              alt={project.alt}
              fill
              className="object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-8">
              <h2 className="font-headline text-4xl font-normal text-white mb-2">
                {project.title}
              </h2>
              <div className="flex items-center space-x-4 text-white/90">
                <div className="flex items-center space-x-2">
                  <Icon name="MapPinIcon" size={20} />
                  <span>{project.location}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="CalendarIcon" size={20} />
                  <span>{project.completionYear}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="p-8">
            <div className="flex items-center space-x-2 mb-6 border-b border-zinc-200">
              <button
                onClick={() => setActiveTab('overview')}
                className={`px-6 py-3 font-medium text-sm transition-all duration-200 ${
                  activeTab === 'overview' ?'text-[#1B5E4F] border-b-2 border-[#1B5E4F]' :'text-black/50 hover:text-black'
                }`}
              >
                Overview
              </button>
              <button
                onClick={() => setActiveTab('process')}
                className={`px-6 py-3 font-medium text-sm transition-all duration-200 ${
                  activeTab === 'process' ?'text-[#1B5E4F] border-b-2 border-[#1B5E4F]' :'text-black/50 hover:text-black'
                }`}
              >
                Design Process
              </button>
              <button
                onClick={() => setActiveTab('gallery')}
                className={`px-6 py-3 font-medium text-sm transition-all duration-200 ${
                  activeTab === 'gallery' ?'text-[#1B5E4F] border-b-2 border-[#1B5E4F]' :'text-black/50 hover:text-black'
                }`}
              >
                Gallery
              </button>
            </div>

            {activeTab === 'overview' && (
              <div className="space-y-8">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-zinc-50 rounded-xl">
                    <div className="flex items-center space-x-2 mb-2">
                      <Icon name="HomeIcon" size={20} className="text-[#1B5E4F]" />
                      <span className="text-xs font-medium text-black/50 uppercase">Type</span>
                    </div>
                    <p className="text-black font-normal">{project.type}</p>
                  </div>
                  <div className="p-4 bg-zinc-50 rounded-xl">
                    <div className="flex items-center space-x-2 mb-2">
                      <Icon name="SparklesIcon" size={20} className="text-[#1B5E4F]" />
                      <span className="text-xs font-medium text-black/50 uppercase">Style</span>
                    </div>
                    <p className="text-black font-normal">{project.style}</p>
                  </div>
                  <div className="p-4 bg-zinc-50 rounded-xl">
                    <div className="flex items-center space-x-2 mb-2">
                      <Icon name="CurrencyRupeeIcon" size={20} className="text-[#1B5E4F]" />
                      <span className="text-xs font-medium text-black/50 uppercase">Budget</span>
                    </div>
                    <p className="text-black font-normal">{project.budget}</p>
                  </div>
                  <div className="p-4 bg-zinc-50 rounded-xl">
                    <div className="flex items-center space-x-2 mb-2">
                      <Icon name="Square3Stack3DIcon" size={20} className="text-[#1B5E4F]" />
                      <span className="text-xs font-medium text-black/50 uppercase">Area</span>
                    </div>
                    <p className="text-black font-normal">{project.area}</p>
                  </div>
                </div>

                <div>
                  <h3 className="font-headline text-2xl font-normal text-black mb-4">Project Description</h3>
                  <p className="text-black/70 font-normal leading-relaxed">{project.detailedDescription}</p>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="p-6 bg-zinc-50 rounded-xl">
                    <h4 className="font-headline text-xl font-normal text-black mb-4">Key Materials</h4>
                    <ul className="space-y-2">
                      {project.materials.map((material, index) => (
                        <li key={index} className="flex items-start space-x-2">
                          <Icon name="CheckCircleIcon" size={20} className="text-[#1B5E4F] mt-0.5" />
                          <span className="text-black/70 font-normal">{material}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="p-6 bg-[#1B5E4F]/5 rounded-xl border border-[#1B5E4F]/20">
                    <div className="flex items-start space-x-3 mb-4">
                      <Icon name="ChatBubbleLeftRightIcon" size={24} className="text-[#1B5E4F] mt-1" />
                      <div>
                        <h4 className="font-headline text-xl font-normal text-black mb-1">Client Testimonial</h4>
                        <p className="text-sm text-black/50">{project.clientName}</p>
                      </div>
                    </div>
                    <p className="text-black/70 font-normal italic leading-relaxed">{project.clientTestimonial}</p>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  {project.gallery && project.gallery.length > 0
                    ? project.gallery.slice(0, 2).map((item, index) => (
                        <div key={index} className="relative h-64 rounded-xl overflow-hidden group">
                          <AppImage
                            src={item.image}
                            alt={item.alt}
                            fill
                            className="object-cover transition-transform duration-500 group-hover:scale-105"
                          />
                        </div>
                      ))
                    : (
                      <div className="relative h-64 rounded-xl overflow-hidden">
                        <AppImage
                          src={project.image}
                          alt={project.alt}
                          fill
                          className="object-cover"
                        />
                      </div>
                    )}
                </div>
              </div>
            )}

            {activeTab === 'process' && (
              <div className="space-y-8">
                <div>
                  <h3 className="font-headline text-2xl font-normal text-black mb-4">Design Challenges</h3>
                  <div className="space-y-3">
                    {project.challenges.map((challenge, index) => (
                      <div key={index} className="flex items-start space-x-3 p-4 bg-zinc-50 rounded-xl">
                        <Icon name="ExclamationTriangleIcon" size={20} className="text-amber-500 mt-0.5" />
                        <p className="text-black/70 font-normal">{challenge}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="font-headline text-2xl font-normal text-black mb-4">Our Solutions</h3>
                  <div className="space-y-3">
                    {project.solutions.map((solution, index) => (
                      <div key={index} className="flex items-start space-x-3 p-4 bg-[#1B5E4F]/5 border border-[#1B5E4F]/20 rounded-xl">
                        <Icon name="LightBulbIcon" size={20} className="text-[#1B5E4F] mt-0.5" />
                        <p className="text-black/70 font-normal">{solution}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="p-6 bg-zinc-50 rounded-xl">
                  <div className="flex items-center space-x-3 mb-2">
                    <Icon name="ClockIcon" size={24} className="text-[#1B5E4F]" />
                    <h3 className="font-headline text-xl font-normal text-black">Project Timeline</h3>
                  </div>
                  <p className="text-black/70 font-normal">{project.timeline}</p>
                </div>
              </div>
            )}

            {activeTab === 'gallery' && (
              <div className="space-y-8">
                {project.videos && project.videos.length > 0 && (
                  <div>
                    <h3 className="font-headline text-2xl font-normal text-black mb-4">Video Walkthroughs</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      {project.videos.map((video, index) => {
                        const videoId = getYouTubeVideoId(video.url);
                        const isPlaying = !!videoId && playingVideoId === videoId;
                        return (
                          <div key={index} className="space-y-2">
                            <div className="relative aspect-[9/16] rounded-xl overflow-hidden bg-black">
                              {isPlaying && videoId ? (
                                <iframe
                                  src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&controls=0&modestbranding=1&rel=0&playsinline=1&iv_load_policy=3&disablekb=1`}
                                  title={video.title}
                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                  allowFullScreen
                                  className="absolute inset-0 w-full h-full border-0"
                                />
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => videoId && setPlayingVideoId(videoId)}
                                  className="absolute inset-0 w-full h-full group"
                                  aria-label={`Play ${video.title}`}
                                >
                                  {videoId && (
                                    // eslint-disable-next-line @next/next/no-img-element
                                    <img
                                      src={`https://i.ytimg.com/vi/${videoId}/maxresdefault.jpg`}
                                      alt={video.title}
                                      className="absolute inset-0 w-full h-full object-cover"
                                      onError={(e) => {
                                        const img = e.currentTarget;
                                        if (!img.src.includes('hqdefault')) {
                                          img.src = `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;
                                        }
                                      }}
                                    />
                                  )}
                                  <div className="absolute inset-0 bg-black/10 group-hover:bg-black/30 transition-colors duration-200" />
                                  <div className="absolute inset-0 flex items-center justify-center">
                                    <div className="w-16 h-16 rounded-full bg-white/95 group-hover:bg-white flex items-center justify-center shadow-lg transition-transform duration-200 group-hover:scale-110">
                                      <svg className="w-7 h-7 text-black ml-1" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M8 5v14l11-7z" />
                                      </svg>
                                    </div>
                                  </div>
                                </button>
                              )}
                            </div>
                            <p className="text-sm font-medium text-black/70">{video.title}</p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {project.gallery.map((item, index) => (
                    <div key={index} className="relative h-64 rounded-xl overflow-hidden group">
                      <AppImage
                        src={item.image}
                        alt={item.alt}
                        fill
                        className="object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectModal;